import { Badge, Flex } from '@radix-ui/themes';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { CopyPitchesDialog } from 'components/common/dialogs/copy-pitches';
import { ErrorBoundary } from 'components/common/error-boundary';
import { ImportPitchersDialog } from 'components/sections/game-data/dialogs/import-pitchers';
import { GameDataGame } from 'components/sections/game-data/games/game';
import { GameDataPlayer } from 'components/sections/game-data/players/player';
import { GameDataTabs } from 'components/sections/game-data/tabs';
import { AimingProvider } from 'contexts/aiming.context';
import { GameDataContext } from 'contexts/game-data.context';
import { t } from 'i18next';
import { tooltipPitchType } from 'lib_ts/enums/pitches.enums';
import { first } from 'lodash';
import { useContext, useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';
import { routePaths } from 'routes/paths';
import { useCookiesStore } from 'stores/use-cookies-store';

export const GameDataRouter = () => {
  const app = useCookiesStore((s) => s.app);

  const {
    dialogImportPitchers,
    dialogAddPitches,
    closeAddDialog,
    closeImportPitchersDialog,
  } = useContext(GameDataContext);

  const addSinglePitch = dialogAddPitches?.pitches.length === 1;

  const firstPitchType = first(dialogAddPitches?.pitches)?.type;

  const buildPriority = useMemo(
    () =>
      PitchDesignHelper.getDisplayBuildPriority(
        app.game_data_import_options.priority
      ),
    [app.game_data_import_options.priority]
  );

  return (
    <ErrorBoundary identifier="GameDataRouter">
      <AimingProvider identifier="GDAimingCx" training={false}>
        <Routes>
          <Route path={routePaths.gameData.index} element={<GameDataTabs />} />

          {/* pitcher */}
          <Route
            path={routePaths.gameData.pitcher}
            element={<GameDataPlayer mode="pitchers" />}
          />

          {/* hitter */}
          <Route
            path={routePaths.gameData.hitter}
            element={<GameDataPlayer mode="hitters" />}
          />

          <Route path={routePaths.gameData.game} element={<GameDataGame />} />
        </Routes>

        {dialogImportPitchers && (
          <ImportPitchersDialog
            key={dialogImportPitchers.key}
            pitchers={dialogImportPitchers.pitchers}
            identifier="GameDataImportPitchersDialog"
            onClose={closeImportPitchersDialog}
          />
        )}

        {dialogAddPitches && (
          <CopyPitchesDialog
            key={dialogAddPitches.key}
            identifier="GameDataAddDialog"
            title={
              <Flex gap="2" align="center">
                {t(
                  dialogAddPitches.isAverage
                    ? 'pd.add-averages-to-pitch-list'
                    : 'pd.add-to-pitch-list'
                )}

                {dialogAddPitches.player && (
                  <Badge>{dialogAddPitches.player.name}</Badge>
                )}

                {addSinglePitch && firstPitchType && (
                  <Badge title={t(tooltipPitchType(firstPitchType)).toString()}>
                    {firstPitchType}
                  </Badge>
                )}

                <Badge>{buildPriority}</Badge>
              </Flex>
            }
            description={t('pl.select-pitch-list-to-copy-into').toString()}
            pitches={dialogAddPitches.pitches}
            onCreated={closeAddDialog}
            onClose={closeAddDialog}
          />
        )}
      </AimingProvider>
    </ErrorBoundary>
  );
};
